import { CalendarDays, ChevronLeft, ChevronRight, Eye, EyeOff } from "lucide-react";
import { useState, type JSX } from "react";

import { ShareDialog } from "../../../common/components/ShareDialog.js";
import { ShareStatusIcon, shareVisibilityLabel, visibilityFromShares } from "../../../common/components/ShareStatusIcon.js";
import { SidebarItem, SidebarSection } from "../../../common/components/SidebarNav.js";
import { useCalendarLocation } from "../calendarLocation.js";
import {
  useCalendarShares,
  useCalendars,
  useRevokeCalendarShare,
  useShareCalendar,
} from "../query/calendarQueries.js";
import { calendarPeriodLabel, isSameDay } from "../weekRange.js";

/**
 * Sidebar der Kalender-App: Wochenwahl und die sichtbaren Kalender. Der
 * Freigabe-Dialog gehört hierher, weil die Freigabe am Kalender hängt — nicht am Termin.
 */
export function CalendarSidebar(): JSX.Element {
  const location = useCalendarLocation();
  const calendars = useCalendars();
  const [shareId, setShareId] = useState<string | null>(null);

  const shares = useCalendarShares(shareId);
  const shareCalendar = useShareCalendar(shareId);
  const revokeShare = useRevokeCalendarShare(shareId);

  const showsToday = location.range.days.some((day) => isSameDay(day, new Date()));

  return (
    <div>
      <SidebarSection title="Zeitraum">
        <div className="mb-1 flex items-center gap-1 px-1">
          <button
            type="button"
            aria-label="Voriger Zeitraum"
            onClick={() => location.move(-1)}
            className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Nächster Zeitraum"
            onClick={() => location.move(1)}
            className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <span className="ml-1 min-w-0 truncate text-sm text-ink">
            {calendarPeriodLabel(location.view, location.anchor, location.range)}
          </span>
        </div>
        <SidebarItem
          label="Heute"
          icon={<CalendarDays className="h-4 w-4" />}
          active={showsToday}
          onClick={() => location.showDate(new Date())}
        />
      </SidebarSection>

      <SidebarSection title="Kalender">
        {(calendars.data ?? []).map((calendar) => {
          const visible = !location.hidden.has(calendar.id);
          return (
            <SidebarItem
              key={calendar.id}
              label={calendar.name}
              hint={calendar.ownedByMe ? null : calendar.ownerDisplayName}
              icon={
                <span
                  aria-hidden="true"
                  className="block h-3 w-3 rounded-full"
                  style={{
                    backgroundColor: visible ? calendar.color : "transparent",
                    border: `2px solid ${calendar.color}`,
                  }}
                />
              }
              active={false}
              onClick={() => location.toggleCalendar(calendar.id)}
              action={
                <span className="flex items-center">
                  <span
                    aria-hidden="true"
                    className="p-1.5 text-ink-muted opacity-0 group-hover:opacity-100"
                  >
                    {visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  </span>
                  {calendar.level === "MANAGE" ? (
                    <CalendarShareButton calendarId={calendar.id} onClick={() => setShareId(calendar.id)} />
                  ) : null}
                </span>
              }
            />
          );
        })}
        {calendars.isPending ? <p className="px-2 text-sm text-ink-muted">Wird geladen …</p> : null}
      </SidebarSection>

      {shareId ? (
        <ShareDialog
          title={shares.data?.calendar.name ?? "Kalender"}
          shares={shares.data?.shares ?? []}
          canManage={(shares.data?.calendar.level ?? "VIEW") === "MANAGE"}
          busy={shareCalendar.isPending || revokeShare.isPending}
          error={shareCalendar.error?.message ?? shares.error?.message ?? null}
          onShare={(input) => shareCalendar.mutate(input)}
          onRevoke={(entry) => revokeShare.mutate(entry)}
          onClose={() => setShareId(null)}
        />
      ) : null}
    </div>
  );
}

function CalendarShareButton({ calendarId, onClick }: { calendarId: string; onClick: () => void }): JSX.Element {
  const shares = useCalendarShares(calendarId);
  const visibility = visibilityFromShares(shares.data?.shares ?? []);
  return <button type="button" onClick={onClick} title={shareVisibilityLabel(visibility)} aria-label={shareVisibilityLabel(visibility)} className="rounded p-1.5 hover:bg-surface-sunken"><ShareStatusIcon visibility={visibility} className="h-3.5 w-3.5" /></button>;
}
